import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { AiCoachSessionState, AiCoachTurnOption, AiCoachTurnPhase } from 'api/AICoachConversationApi';
import { postAiCoachConversation } from 'thunks';

export enum AiCoachConversationRole {
  Assistant = 'assistant',
  User = 'user',
}

export interface AiCoachConversationEntry {
  role: AiCoachConversationRole;
  text: string;
}

export interface AiCoachConversationState {
  entries: AiCoachConversationEntry[];
  options: AiCoachTurnOption[];
  terminal: boolean;
  phase?: AiCoachTurnPhase;
  isLoading: boolean;
  isError: boolean;
  sessionState: AiCoachSessionState | null;
  lastOptionId?: string;
  started: boolean;
}

const initialState: AiCoachConversationState = {
  entries: [],
  options: [],
  terminal: false,
  isLoading: false,
  isError: false,
  sessionState: null,
  started: false,
};

const aiCoachConversation = createSlice({
  name: 'aiCoachConversation',
  initialState,
  reducers: {
    resetAiCoachConversation: () => initialState,
    addAiCoachUserChoice: (state, action: PayloadAction<string>) => {
      state.entries.push({ role: AiCoachConversationRole.User, text: action.payload });
    },
  },
  extraReducers: (builder) => {
    builder.addCase(postAiCoachConversation.pending, (state, action) => {
      state.isLoading = true;
      state.isError = false;
      state.options = [];
      state.started = true;
      state.lastOptionId = action.meta.arg.optionId;
    });
    builder.addCase(postAiCoachConversation.fulfilled, (state, { payload }) => {
      const { turn, state: sessionState } = payload;
      state.isLoading = false;
      state.sessionState = sessionState;
      turn.messages.forEach((text) => state.entries.push({ role: AiCoachConversationRole.Assistant, text }));
      state.options = turn.terminal ? [] : turn.options;
      state.terminal = turn.terminal;
      state.phase = turn.phase;
    });
    builder.addCase(postAiCoachConversation.rejected, (state) => {
      state.isLoading = false;
      state.isError = true;
    });
  },
});

export const { resetAiCoachConversation, addAiCoachUserChoice } = aiCoachConversation.actions;

export default aiCoachConversation.reducer;
